<div class="absolute inset-0 flex flex-col lg:flex-row">

  <!-- Left pane: title, search, tabs, date picker, legend (desktop sidebar / mobile top bar) -->
  <aside
    class="lg:w-80 lg:shrink-0 lg:border-r border-gray-200/60 lg:bg-white/50 lg:backdrop-blur-sm flex flex-col">
    <div class="p-3 sm:p-4 lg:p-6 flex flex-col gap-3 lg:gap-4">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h1 class="text-xl sm:text-2xl xl:text-3xl font-bold tracking-tight text-gray-900">Kalender</h1>
          <p class="text-sm text-gray-500">{{ today | date: "EEEE, dd. MMMM yyyy" }}</p>
        </div>

        <!-- mobile: reveal the month picker to filter by date -->
        <button type="button" (click)="toggleCalendar()"
                class="lg:hidden inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                [ngClass]="{ 'bg-theme-primary-50 border-theme-primary-200 text-theme-primary-700': calendarOpen }">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
          </svg>
          {{ eventService.selectedDay.date | date: "dd.MM." }}
        </button>
      </div>

      <input [(ngModel)]="search" type="text" placeholder="Suchen…"
             class="block w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 placeholder-gray-300">

      <!-- tabs: my shifts / all / open (unanswered) — fill the width, three equal segments -->
      <div class="flex w-full rounded-lg bg-gray-100 p-1">
        <button type="button" (click)="tab = 'mine'"
                class="flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
                [ngClass]="tab === 'mine' ? 'bg-white text-theme-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'">
          Meine
        </button>
        <button type="button" (click)="tab = 'all'"
                class="flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
                [ngClass]="tab === 'all' ? 'bg-white text-theme-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'">
          Alle
        </button>
        <button type="button" (click)="tab = 'open'"
                class="flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
                [ngClass]="tab === 'open' ? 'bg-white text-theme-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'">
          Offen
        </button>
      </div>

      <!-- month picker: collapsed on mobile (toggle above), always visible on desktop -->
      @if (todayKey) {
        <app-small-calendar (dateRangeChange)="onDateRangeChange($event)"
                            (selectedDateChange)="onSelectedDateChange($event)"
                            [(dateRange)]="eventService.calendarRange"
                            [(selectedDate)]="eventService.selectedDay"
                            [calendarEntries]="eventService.calendarEntries"
                            [todayKey]="todayKey"
                            class="w-full lg:block" [class.hidden]="!calendarOpen"/>
      }

      <!-- shard legend (desktop only, keeps the mobile top bar compact) -->
      @if (eventService.events_all) {
        <div class="hidden lg:flex flex-col gap-1.5">
          @for (shard of getShards(eventService.events_filtered); track shard.item1) {
            <div class="flex items-center gap-2">
              <span [style.background-color]="shard.item2" class="h-2.5 w-2.5 rounded-full"></span>
              <span class="text-sm text-gray-600">{{ shard.item1 }}</span>
            </div>
          }
        </div>
      }

      <!-- export ALL assigned shifts to the calendar (.ics) -->
      @if (shifts.length > 0) {
        <button type="button" (click)="IcsService.download(shifts)"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-theme-primary-700 shadow-sm transition-colors hover:bg-theme-primary-50">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4"/>
          </svg>
          Dienste in den Kalender übernehmen
        </button>
      }
    </div>
  </aside>

  <!-- Right pane: day-grouped event list -->
  <main class="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 lg:p-6 relative">

    <!-- loading -->
    @if (eventService.events_all === undefined) {
      <div class="absolute h-10 w-10 text-theme-primary-600 animate-spin left-1/2 -ml-5 top-24">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path clip-rule="evenodd"
                  d="M12 19a7 7 0 1 0 0-14a7 7 0 0 0 0 14m0 3c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10"
                  fill-rule="evenodd" opacity="0.2"/>
            <path d="M2 12C2 6.477 6.477 2 12 2v3a7 7 0 0 0-7 7z"/>
          </g>
        </svg>
      </div>
    }

    <!-- empty -->
    @if (eventService.events_all !== undefined && grouped().length === 0) {
      <p class="text-sm text-gray-400 text-center py-16">Keine Veranstaltungen gefunden.</p>
    }

    <!-- grouped by day: each event renders its full card (accept / decline / comment inline) -->
    @for (group of grouped(); track group.key) {
      <div class="mb-6">
        <h3 class="text-sm font-semibold text-gray-500 mb-2 px-1">{{ group.day | date: "EEEE, dd. MMMM" }}</h3>
        <div class="flex flex-col gap-3">
          @for (event of group.events; track event.id) {
            <app-event-display [event]="event" [responses]="shifts_getForEvent(event.id)"></app-event-display>
          }
        </div>
      </div>
    }
  </main>
</div>
